/* Whether the on-screen keyboard is up, so the tab bar can step out of its way.
 *
 * Android shrinks the page when the keyboard opens, and the tab bar is pinned to
 * the bottom of the page, so it rode up on top of the keyboard — covering the
 * search results the keyboard had been opened to type into. There is no event
 * for the keyboard itself, so it is inferred from two things together: a field
 * that takes typing has focus, and the visible height has dropped well below
 * the tallest it has been. Focus alone is not enough — Android's back button
 * closes the keyboard and leaves the field focused, and a desktop has no
 * on-screen keyboard at all. */

/** Input types that bring up the keyboard. A date, a checkbox or a slider open
    a picker or nothing, and must not hide the tab bar. */
const TYPED = new Set(['', 'text', 'search', 'number', 'email', 'tel', 'url', 'password']);

export interface FocusTarget {
  tagName: string;
  type?: string;
  isContentEditable?: boolean;
  readOnly?: boolean;
}

export function summonsKeyboard(el: FocusTarget | null): boolean {
  if (!el) return false;
  if (el.isContentEditable) return true;
  const tag = el.tagName.toUpperCase();
  if (el.readOnly) return false;
  if (tag === 'TEXTAREA') return true;
  return tag === 'INPUT' && TYPED.has((el.type ?? '').toLowerCase());
}

/** A keyboard takes a third or more of a phone's height; a browser's own bars
    coming and going take far less. */
const KEYBOARD_SHARE = 0.8;

export function keyboardOpen(typing: boolean, visibleHeight: number, tallestHeight: number): boolean {
  return typing && tallestHeight > 0 && visibleHeight < tallestHeight * KEYBOARD_SHARE;
}

/** Keeps `data-keyboard="open"` on the root element while the keyboard is up. */
export function watchKeyboard(): void {
  const root = document.documentElement;
  const height = () => window.visualViewport?.height ?? window.innerHeight;
  let tallest = height();
  let width = window.innerWidth;

  const update = () => {
    // A rotation changes what "full height" means; start measuring again.
    if (window.innerWidth !== width) { width = window.innerWidth; tallest = height(); }
    const now = height();
    const typing = summonsKeyboard(document.activeElement as FocusTarget | null);
    if (!typing) tallest = Math.max(tallest, now);
    if (keyboardOpen(typing, now, tallest)) root.dataset.keyboard = 'open';
    else delete root.dataset.keyboard;
  };

  window.visualViewport?.addEventListener('resize', update);
  window.addEventListener('resize', update);
  document.addEventListener('focusin', update);
  // Focus leaves before the keyboard has finished closing; look again after.
  document.addEventListener('focusout', () => setTimeout(update, 0));
}
